/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getScrollBarSize } from "utils/DomUtils";
import { EnableFeatureController } from "app/EnableFeatureController";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import {
  getAssetUri,
  shouldUseLinearization,
  getQueryParamValue,
  isFillSignEnabled,
  addSABOriginTrialMetaTag,
  isSABAvailable
} from "app/AppInitUtility";
import docApi from "api";
import { dialogApi } from "components/dialogs";
import { isCurrentDeviceMobile, isCurrentDeviceTablet } from "dc-preview-utils/src/Utils/DeviceUtils";
import { keyboardHandler, resetKeyboard } from "utils/keyboard";
import { unsubscribeFromSelectors, subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { apiFactory } from "api/external/ApiFactory";
import { trackDocumentClose, trackViewerInit, resetDocumentId, setDocumentId } from "analytics/events";
import { pageContentHandler } from "pageContentHandler";
import { contextMenuManager } from "contextMenuManager";
import { recordPreviewInit, resetPerfTimings } from "utils/performance";
import { pinchZoomManager } from "pinchZoom/index";
import { overlayEvents } from "overlays/OverlayEvents";
import { printHandler } from "print/printHandler";
import { classicPDFSearchManager } from "search/ClassicPDFSearchManager";
import { getCurrentPageNumber } from "selectors/pageNumber";
import {
  getShouldUseModernViewer,
  getJSEngine,
  getJsSAB,
  getShouldEnableOutline,
  getShouldEnableQnA
} from "selectors/appSelectors";
import { cancelAllPromises } from "dc-preview-utils/src/Utils/AJSCancellablePromise";
import { resetPDFActionListeners } from "pdfActionManager/listenToPDFAction";
import { AccessibilityManager } from "accessibility/AccessibilityManager";
import commandManager from "undoRedo/CommandManager";
import { registerDocumentApis } from "publicApis/DocumentApis";
import { registerAppApis } from "publicApis/AppApis";
import { registerHUDApis } from "publicApis/HUD";
import { registerLHPApis } from "publicApis/LHP";
import { registerRHPApis } from "publicApis/RHP";
import { registerFillAndSignApis } from "publicApis/FillAndSignApis";
import { registerQuickToolsApis } from "publicApis/QuickTools";
import { registerStickyToolsApis } from "publicApis/StickyTools";
import { registerNavigationBarApis } from "publicApis/NavigationBar";
import { registerFTEApis } from "publicApis/FTEApis";
import { registerLiquidModeApis } from "publicApis/LiquidModeApi";
import * as oldPublicApis from "publicApis/OldPublicApis";
import { hudHideManager, bottomBoardHideManager } from "utils/ComponentHideManager";
import { resetPreviewEvents, triggerCurrentPageChangeEvent } from "publicApis/Events/events";
import { featureLifecycleHookController } from "publicApis/controller/featureLifecycleHookController";
import { RenderingManager } from "app/RenderingManager";
import { providers } from "dc-core";
import { initializeModernViewerContextMenu } from "utils/ContextMenuUtils";
import { getViewMode } from "selectors/viewModeSelectors";
import { docViewModes } from "constants/enums";
import { OverlaysManager } from "api/OverlaysManager";
import hcThemeProvider from "../highContrast/HCThemeProvider";
import { executeDocWillCloseActions } from "pdfActionManager/docActionsHelper";
import { pageChangeHandler } from "utils/PageActionsUtils";
import {
  getIsDocOpenActionsCompleted,
  shouldLogGetPageRenditionInfo,
  getPageRenditionCallInfo,
  getCacheBurstInfoForLogging
} from "selectors/documentSelectors";
import threadHostSimulator from "javascript/jsEngine/threadHostSimulator";
import {
  getShouldRenderEditDropin,
  getShouldRenderCropPagesDropin,
  getShouldRenderNumberPagesDropin
} from "selectors/overlayDropinSelectors";
import { resetInHouseLogging } from "logging/performance";
import { logGetPageRenditionCallInfo, logMaxZoomReached, logCacheBurstInfo } from "logging/index";
import { isMaxZoomReached } from "selectors/zoom";
import RemountManager from "./RemountManager";
import { registerBottomBoardApis } from "publicApis/BottomBoard";
import { getFeatureFlagApis } from "publicApis/featureFlag";
import { registerFABApis } from "publicApis/FAB";
import { getSharedArrayBuffer } from "providers/previewProvider/PreviewProviderUtils";
import { registerTopPanelApis } from "publicApis/TopPanel";
import { registerFloatingWindowsApis } from "publicApis/FloatingWindowsApis";

class AppController {
  constructor(stores, props) {
    this._setDocumentIDForAsset(props);
    recordPreviewInit();
    this.overlayDropinLoaded = false;
    this.maxZoomInfoLogged = false;
    this.stores = stores;
    this.stores.appStore.appController = this;
    this.props = props;
    this._setShouldUseModernViewer();
    this._setShouldUseMobileModernViewer();
    this._setFontAccessType();
    this._setEnableEditForFreeUsers();
    this._setPageLoaderFalse();
    this._setCommentsAdditionalConfig();

    this._keyboardHandler = keyboardHandler;

    this.stores.initStore(docApi, this.stores);

    this._setEnableLinearization();

    this._setEnableCNPDF();

    this._setScrollbarSize();

    this._setUseViewSDKForAcroformSubmit();

    this.enableFeatureController = new EnableFeatureController(stores, props);
    this._setEditEnterInfo(this.enableFeatureController.props);
    this.enableFeatureController.initFeatures();

    this.accessibilityManager = new AccessibilityManager(this.stores);

    this._setAppContext(this.props.config.context);

    this._resetDocumentCloseTracking = this._setUpDocumentCloseTracking();

    this._setAddressbarHeight();
    this._initializeRightClickContextMenu(stores);

    this.renderingManager = new RenderingManager(this.stores, this.props);

    dialogApi.setIntl(props.intl);
    this._setModernViewerFlags();

    const shouldUseModernViewer = getShouldUseModernViewer(this.stores);
    if (shouldUseModernViewer) {
      initializeModernViewerContextMenu(this.props.config.filetype);
      if (!this.props.config.shouldUseMobileModernViewer) {
        this.stores.highContrastStore.setHCFlagEnabled(true);
      }
    }
    trackViewerInit();

    this._configureBusyLoop();
    this._setKillSwitchJSOverridePromise();
    this._setSupportPDFJavaScriptPromise();
    this._setCustomLoader();
    this._setLinearizationBlockSize();
    this._setFileLocation();
    this._setFillSignConfig();
    this._setShowPremiumIconForNonDCWebClients();
    this._setEnableDigSig();
    this._setShowAssistantInRHP();
    this._setBottomBannerConfig();
  }

  _setShowAssistantInRHP() {
    this.stores.appStore.setShowAssistantInRHP(this.props.config.showAssistantInRHP);
  }

  async resetAssetURI(props) {
    this._setDocumentIDForAsset(props);
    await this.renderingManager.resetAssetURIInStore(props);
  }

  init(switchPreview = false) {
    const overlaysManager = new OverlaysManager(apiFactory, this.stores.overlayDropinStore.addOverlayDropin);
    this.stores.appStore.setOverlaysManager(overlaysManager);

    this.renderingManager.init(switchPreview);
    this._subscribeExternalComponentsForOverFlowMenu();

    this.stores.appStore.setIntl(this.props.intl);

    this._registerOldPublicApis();
    this._registerPublicApis();

    subscribeToSelector(this.stores, getCurrentPageNumber, pageNum => {
      triggerCurrentPageChangeEvent(pageNum);
      if (pageNum > 0 && this.stores.documentStore.getPageRenditionInfo)
        this.stores.documentStore.getPageRenditionInfo.pageNumUpdateCount++;
      // Page change handler is called before doc open actions are executed, which is not the desired behaviour,
      // so for first page load, we wait for doc actions completed and then execute page action for current page
      // in file docActionsHelper - executeDocOpenActions
      // For other pages, since docopen will already be handled, so it will executed normally.
      if (getIsDocOpenActionsCompleted(this.stores)) {
        pageChangeHandler(this.stores, pageNum);
      }
    });

    subscribeToSelector(this.stores, isMaxZoomReached, maxZoomReached => {
      if (maxZoomReached && !this.maxZoomInfoLogged) {
        this.maxZoomInfoLogged = true;
        logMaxZoomReached();
      }
    });

    window.addEventListener("keydown", this._keyboardHandler);
    commandManager.undoStackCleared = false;

    providers["keyboard-shortcuts-provider"]().then(keyboardShortcutProvider => keyboardShortcutProvider.subscribe());

    RemountManager.setUnmountPromise();
  }

  getExposedApis(clientName, requestedApiList, isNew = false) {
    return apiFactory.getExposedApis(clientName, requestedApiList, true, isNew);
  }

  _isEditModuleLoaded() {
    return (
      getShouldRenderCropPagesDropin(this.stores) ||
      getShouldRenderNumberPagesDropin(this.stores) ||
      getShouldRenderEditDropin(this.stores)
    );
  }

  cleanup(switchPreview = false) {
    /**
     * Cancel all promises once the app is unmounted so that no .then and .catch callbacks are called.
     * This is needed as the callbacks may have side effects or may depend on external objects
     * which may no longer exist after the unmount.
     */
    let cleanupPromise = Promise.resolve();
    if (shouldLogGetPageRenditionInfo(this.stores)) {
      logGetPageRenditionCallInfo(getPageRenditionCallInfo(this.stores));
    }
    logCacheBurstInfo(getCacheBurstInfoForLogging(this.stores));
    this.renderingManager.resetTimeout(this.getPageRenditionLogTimeout);
    const jsEngine = getJSEngine(this.stores);
    jsEngine && jsEngine.reset();

    let unloadEditPromise = Promise.resolve(true);
    if (!switchPreview && this._isEditModuleLoaded()) {
      unloadEditPromise = providers["edit-provider"]().then(editProvider => editProvider.unloadEditModule());
    }
    this._unsubscribeExternalComponentsForOverFlowMenu();
    cancelAllPromises();

    providers["keyboard-shortcuts-provider"]().then(keyboardShortcutProvider => {
      keyboardShortcutProvider.reset();
      keyboardShortcutProvider.unsubscribe();
    });

    resetKeyboard();
    resetPerfTimings();
    resetInHouseLogging();
    this.stores.readAloudStore.sendDurationPlayedAnalytics();
    trackDocumentClose();
    unsubscribeFromSelectors();
    this._resetDocumentCloseTracking && this._resetDocumentCloseTracking();
    this.enableFeatureController.unregisterScrollEventFromViewSDK();
    window.removeEventListener("keydown", this._keyboardHandler);
    this.stores.resetStore(switchPreview);
    this.stores.initStore(docApi, this.stores);
    dialogApi.reset();
    featureLifecycleHookController.reset();
    if (!switchPreview) cleanupPromise = unloadEditPromise.finally(() => docApi.reset());
    apiFactory.reset();
    contextMenuManager.reset();
    overlayEvents.reset();
    if (!switchPreview) resetPreviewEvents();
    pageContentHandler.reset();
    pinchZoomManager.resetState();
    printHandler.reset();
    classicPDFSearchManager.init();
    commandManager.reset();
    hudHideManager.reset();
    hcThemeProvider.reset();
    resetPDFActionListeners();
    resetDocumentId();
    this.accessibilityManager.reset();
    RemountManager.setIsUnmounting(cleanupPromise);
    bottomBoardHideManager.reset();

    // Cleanup floating windows to prevent memory leaks
    const floatingWindowsManager = require("FloatingWindow/FloatingWindowsManager").default;
    if (floatingWindowsManager) {
      floatingWindowsManager.cleanup();
    }
  }

  _registerPublicApis() {
    registerAppApis(this.stores);
    registerDocumentApis(this.stores);
    registerHUDApis(this.stores);
    registerLHPApis(this.stores);
    registerRHPApis(this.stores);
    isFillSignEnabled(this.props.config).then(enableFillSign => {
      if (enableFillSign) registerFillAndSignApis(this.stores);
    });
    registerQuickToolsApis(this.stores);
    registerStickyToolsApis(this.stores);
    registerNavigationBarApis(this.stores);
    registerFTEApis(this.stores);
    registerLiquidModeApis(this.stores);
    registerBottomBoardApis(this.stores);
    registerFABApis(this.stores);
    registerFloatingWindowsApis(this.stores);
    registerTopPanelApis(this.stores);
  }

  _registerOldPublicApis() {
    Object.keys(oldPublicApis).forEach(key => {
      const apiSuite = oldPublicApis[key];
      const { apiName, apiGetter } = apiSuite(this.stores, this.props.config);
      apiFactory.registerApi(apiName, apiGetter);
    });
  }

  _setDocumentIDForAsset(props) {
    if (props.config && props.config.alternateAssetUri) {
      setDocumentId(decodeURIComponent(props.config.alternateAssetUri));
    } else {
      setDocumentId(decodeURIComponent(props.config.assetUri || getAssetUri()));
    }
  }

  _setCustomLoader() {
    const { enableCustomLoader } = this.props.config;
    enableCustomLoader && this.stores.appStore.setCustomLoader(enableCustomLoader);
  }

  _setFontAccessType() {
    const { fontAccessType } = this.props.config;
    this.stores.appStore.setFontAccessType(fontAccessType);
  }

  _setEnableEditForFreeUsers() {
    const { enableEditForFreeUsers } = this.props.config;
    this.stores.appStore.setEnableEditForFreeUsers(enableEditForFreeUsers);
  }

  _setShouldUseModernViewer() {
    const { shouldUseModernViewer } = this.props.config;
    this.stores.appStore.setShouldUseModernViewer(shouldUseModernViewer);
  }

  _setShouldUseMobileModernViewer() {
    const { shouldUseMobileModernViewer } = this.props.config;
    this.stores.appStore.setShouldUseMobileModernViewer(shouldUseMobileModernViewer);
  }

  _setPageLoaderFalse() {
    const { editDeepLink } = this.props.config;
    // Won't show page loader if coming from deep link
    if (editDeepLink) this.stores.documentStore.setShouldShowPageLoader(false);
  }

  _setCommentsAdditionalConfig() {
    const { commentsAdditionalConfig } = this.props.config;
    this.stores.appStore.setCommentsAdditionalConfig(commentsAdditionalConfig);
  }

  _setModernViewerFlags() {
    /** Set global flags in store specific to modern viewer in this function
     */

    /** Adding flag to use modern context menu */
    const shouldUseModernViewer = getShouldUseModernViewer(this.stores);
    const viewMode = getViewMode(this.stores);
    if (shouldUseModernViewer && viewMode !== docViewModes.IN_PLACE && viewMode !== docViewModes.SINGLE_PAGE) {
      this.stores.hudStore.setHudDisable(true);
      this.stores.hudStore.setHudExplicitlyDisabled(true);
    }
  }

  _setEnableLinearization() {
    this.stores.appStore.setEnableLinearization(shouldUseLinearization(this.props));
  }

  _setEnableCNPDF() {
    this.stores.appStore.setEnableCNPDF(this.props.config.enableCNPDF);
  }

  _setEnableDigSig() {
    const enableDigSig = getQueryParamValue("enableDigitalSignatures");
    if (enableDigSig !== undefined) {
      this.stores.digSigStore.setEnableDigSig(enableDigSig === "true");
    }
  }

  _setScrollbarSize() {
    this.stores.appUiState.setScrollbarSize(getScrollBarSize());
  }

  _setUseViewSDKForAcroformSubmit() {
    let { useViewSDKForAcroformSubmit } = this.props.config;
    this.stores.appStore.setUseViewSDKForAcroformSubmit(useViewSDKForAcroformSubmit);
  }

  _subscribeExternalComponentsForOverFlowMenu() {
    this.stores.hudStore.subscribeExternalComponentsForOverFlowMenu();
  }

  _unsubscribeExternalComponentsForOverFlowMenu() {
    this.stores.hudStore.unsubscribeExternalComponentsForOverFlowMenu();
  }

  _setAppContext(context) {
    this.stores.appUiState.setAppContext(context);
  }

  _initializeRightClickContextMenu(stores) {
    let { shouldDisableDownloadInContextMenu } = this.props.config;
    stores.appStore.initializeRightClickContextMenu(shouldDisableDownloadInContextMenu);
  }

  _setAddressbarHeight() {
    if (isCurrentDeviceMobile() || isCurrentDeviceTablet()) {
      this.stores.appUiState.setAddressBarHeight(document.body.clientHeight - window.innerHeight);
    }
  }

  _setEditEnterInfo(enterEditInfo) {
    const { UIControlsConfig: { FIRST_TIME_ENTER_EDIT: { firstTimeEnterEdit = {} } = {} } = {} } = enterEditInfo || {};
    this.stores.appStore.setEditEnterInfo(firstTimeEnterEdit);
    getFeatureFlagApis()
      .getFeatureFlag("edit-config")
      .then(editConfig => {
        this.stores.appStore.setEditEnterInfo(editConfig.firstTimeEnterEdit);
      });
  }

  _setUpDocumentCloseTracking() {
    return addEventListener(window, "beforeunload", () => {
      executeDocWillCloseActions(this.stores);
      trackDocumentClose();
      return undefined;
    });
  }

  _configureBusyLoop() {
    let jsSAB = getSharedArrayBuffer();
    if (!jsSAB) {
      addSABOriginTrialMetaTag();
      if (isSABAvailable()) {
        jsSAB = new SharedArrayBuffer(1024);
      }
    }
    if (isSABAvailable()) {
      this.stores.appStore.setJsSAB(jsSAB);
      threadHostSimulator.init();
    }
  }

  _setKillSwitchJSOverridePromise() {
    const { enablePDFJavaScriptOverridePromise } = this.props.config;
    this.stores.appStore.setKillSwitchJSOverridePromise(enablePDFJavaScriptOverridePromise);
  }

  _setSupportPDFJavaScriptPromise() {
    const { enablePDFJavaScriptOverridePromise } = this.props.config;

    const finalPromise = enablePDFJavaScriptOverridePromise.then(supportPDFJavaScript => {
      if (!getJsSAB(this.stores)) {
        supportPDFJavaScript = false;
      }

      this.stores.appStore.setSupportPDFJavaScript(supportPDFJavaScript);

      return supportPDFJavaScript;
    });

    this.stores.appStore.setSupportPDFJavaScriptPromise(finalPromise);
  }

  _setLinearizationBlockSize() {
    const { linearizationBlockSize } = this.props.config;
    this.stores.appStore.setLinearizationBlockSize(linearizationBlockSize);
  }

  _setFileLocation() {
    const { filePath } = this.props.config;
    if (filePath) {
      this.stores.appStore.setFileLocation(filePath);
    }
  }

  _setFillSignConfig() {
    const { fillSignConfig = {} } = this.props.config;
    this.stores.appStore.setFillSignConfig(fillSignConfig);
  }
  _setShowPremiumIconForNonDCWebClients() {
    const { showPremiumIconForNonDCWebClients = false } = this.props.config;
    this.stores.appStore.setShowPremiumIconForNonDCWebClients(showPremiumIconForNonDCWebClients);
  }

  _setBottomBannerConfig() {
    const { bottomBannerConfig } = this.props.config;
    this.stores.bottomBoard.setBottomBannerConfig(bottomBannerConfig);
  }
}
export { AppController };
